import { CHANNEL_TYPES } from '@/common/constants/channel';
import store from "@/store";
import { findChannel, findChannelSync } from '_js/find-channel'
import { getRoomInfo } from '_api/room';

// 通过room_id和channel_id 获取频道的默认通知设置
export function getDefaultNotifyByChannelId(room_id, channel_id) {
  let all_notify_settings = store.getters.all_notify_settings || {}
  let room_s = all_notify_settings[room_id] || {} // 每个房间是一定存在的
  const text_channels = room_s.text_channels ?? []
  const is_audio_channel = !text_channels.includes(channel_id)
  return getDefaultNotifySetting(is_audio_channel)
}

// 通过频道类型获取频道的默认通知设置
function getDefaultNotifyByChannelType(type) {
  return getDefaultNotifySetting(CHANNEL_TYPES.has_audio_types.includes(type))
}

function getDefaultNotifySetting(isAudio) {
  if (isAudio) {
    return JSON.parse(JSON.stringify(store.getters.default_notify_settings.audio_channel_default_setting))
  } else {
    return JSON.parse(JSON.stringify(store.getters.default_notify_settings.default_channel_setting))
  }
}

function mergeDefaultChannelSetting(data, channel) {
  let default_setting = getDefaultNotifyByChannelType(channel.channel_type)
  const obj = {
    room_id: channel.room_id,
    channel_id: channel.channel_id || '',
  }
  return Object.assign(default_setting, data, obj)
}

export const getStoredNotifySetting = (room_id, channel_id) => {
  let all_notify_settings = store.getters.all_notify_settings || {}
  // 获取房间的通知设置直接赋值
  if (!channel_id && !room_id) return {}
  let room_s = all_notify_settings[room_id] || {} // 每个房间是一定存在的
  if (!channel_id) {
    return room_s
  }
  const text_channels = room_s.text_channels ?? []
  const is_audio_channel = !text_channels.includes(channel_id)
  const room_channel_s = room_s.channels // 房间里面的频道可能不存在
  if (!room_channel_s) {
    const data = getDefaultNotifySetting(is_audio_channel)
    return data
  }
  const group = room_channel_s.find((group) => group.channel_id === channel_id)
  if (group) {
    return group
  }
  const channel = findChannelSync(channel_id, room_channel_s || [], false, false)
  return channel || getDefaultNotifySetting(is_audio_channel)
}

// 用于设置通知设置
export const setNotifySetting = async (room_id, channel_id, data = {}) => {
  // 验证输入参数
  if (!room_id) {
    throw new Error('room_id is required');
  }
  if (!data) {
    throw new Error('data is required');
  }

  // 获取当前所有的通知设置
  let all_notify_settings = store.getters.all_notify_settings || {};
  // 获取指定房间的设置，如果不存在则创建一个空对象
  let room_settings = all_notify_settings[room_id] || {};

  // 如果没有指定频道ID，则直接更新房间设置
  if (!channel_id) {
    all_notify_settings[room_id] = { ...room_settings, ...data, room_notify_set: true };
  } else {
    // 如果指定了频道ID，则调用函数处理频道相关的设置
    await updateRoomSettingsWithChannel(all_notify_settings, room_id, room_settings, channel_id, data);
  }
  // 更新全局的通知设置
  store.commit('SET_ALL_NOTIFY_SETTINGS', all_notify_settings);
}

// 处理具有频道ID的房间设置更新
async function updateRoomSettingsWithChannel(all_notify_settings, room_id, room_settings, channel_id, data) {
  // 查找频道信息，如果找不到则抛出错误
  const cur_room_data = store.getters.cur_room_data
  let channels
  if(room_id !== cur_room_data.room_id) {
    let res = await getRoomInfo({room_id})
    if(res.data.status === 'ok') {
      channels = res.data.result.room_info.channels
    }
  }
  const channel = await findChannel(channel_id);
  if (!channel) {
    return
  }
  const parent_channel = channel.parent_id && await findChannel(channel.parent_id, channels)

  // 先判断有没有对应频道的设置，没有的话merge default
  const notify_channel = findChannelSync(channel_id, room_settings.channels || [], false, false);
  if (notify_channel) {
    data = Object.assign(notify_channel, data);
  } else {
    data = mergeDefaultChannelSetting(data, channel, all_notify_settings);
  }
  // 如果房间设置中没有频道信息或频道列表为空，则初始化频道列表
  if (!room_settings.channels || !room_settings.channels.length) {
    initRoomChannels(room_settings, channel, parent_channel, data);
  } else {
    // 如果已存在频道列表，则更新现有频道信息
    updateExistingRoomChannels(room_settings, channel, parent_channel, data, channel_id);
  }

  // 更新房间设置
  all_notify_settings[room_id] = room_settings;
}

// 初始化房间频道列表
function initRoomChannels(room_settings, channel, parent_channel, data) {
  // 创建一个新的频道列表
  // 如果频道有父频道ID，则创建一个包含子频道列表的父频道对象
  if (channel.parent_id) {
    if (parent_channel) {
      // 如果parent_channel数据存在，让all_notify_settings[room_id].channel_list中的数据和此房间真实channel_list中一致
      const parent_channel_list = store.getters.channels.filter(item => item.parent_id === parent_channel.channel_id)
      room_settings.channels = [{
        channel_id: channel.parent_id,
        channel_type: channel.channel_type,
        channel_list: parent_channel_list?.map((in_channel) => {
          if (in_channel.channel_id === channel.channel_id) {
            return data
          } else {
            return {
              channel_id: in_channel.channel_id,
            }
          }
        }) || []
      }];
    } else {
      // 如果parent_channel不存在，说明是数据异常情况，忽略分组中的其他频道
      room_settings.channels = [{
        channel_id: channel.parent_id,
        channel_type: channel.channel_type,
        channel_list: [data],
      }];
    }
  } else {
    const channel_sub_list = store.getters.channels.filter(item => item.parent_id === channel.channel_id)
    room_settings.channels = [{
      channel_id: data.channel_id,
      dnds: data.dnds,
      flags: data.flags,
      room_id: room_settings.room_id,
      notify_type: data.notify_type,
      channel_list: channel_sub_list?.map(({channel_id}) => {
        return {
          channel_id
        }
      })  || [],
    }];
  }
}

// 更新现有的房间频道列表
function updateExistingRoomChannels(room_settings, channel, parent_channel, data, channel_id) {
  // 如果频道有父频道ID，则更新或添加到父频道的子频道列表中
  if (channel.parent_id) {
    let target_group = room_settings.channels.find(group => group.channel_id == channel.parent_id);
    if (target_group) {
      // 父频道对象存在
      const target_channel_list = target_group.channel_list || [];
      let target_channel = target_channel_list.find(ch => ch.channel_id == channel_id);
      if (target_channel) {
        Object.assign(target_channel, data);
      } else {
        target_channel_list.push(data);
      }
      target_group.channel_list = target_channel_list;
    } else {
      // 父频道对象不存在
      room_settings.channels.push({
        channel_id: channel.parent_id,
        channel_type: channel.channel_type,
        channel_list: [data]
      });
      if (parent_channel) {
        // 如果parent_channel数据存在，让all_notify_settings[room_id].channel_list中的数据和此房间真实channel_list中一致
        room_settings.channels.push({
          channel_id: channel.parent_id,
          channel_type: channel.channel_type,
          channel_list: parent_channel.channel_list?.map((in_channel) => {
            if (in_channel.channel_id === channel.channel_id) {
              return data
            } else {
              return {
                channel_id: in_channel.channel_id,
              }
            }
          }) || []
        });
      } else {
        // 如果parent_channel不存在，说明是数据异常情况，忽略分组中的其他频道
        room_settings.channels.push({
          channel_id: channel.parent_id,
          channel_type: channel.channel_type,
          channel_list: [data],
        });
      }
    }
  } else {
    // 如果没有父频道ID，则直接更新或添加频道到房间频道列表中
    let target_channel = room_settings.channels.find(channel => channel.channel_id == channel_id);
    if (target_channel) {
      // 如果找到目标频道，则更新其设置
      Object.assign(target_channel, data);
    } else {
      // 如果没有找到目标频道，则将新的频道数据添加到房间频道列表中
      room_settings.channels.push(data);
    }
  }
}


export function checkIsMute (room_id, channel_id) {
  let left_mute_time = 0
  if (channel_id) {
    const select_channel_notify_settings = getStoredNotifySetting(room_id, channel_id)
    left_mute_time = select_channel_notify_settings?.dnds?.until ? Number(select_channel_notify_settings?.dnds?.until) : 0
  } else {
    const select_room_notify_settings = getStoredNotifySetting(room_id)
    left_mute_time = select_room_notify_settings?.dnds?.until ? Number(select_room_notify_settings?.dnds?.until) : 0
  }
  const second = left_mute_time > 0 ? (left_mute_time - Date.parse(new Date()) / 1000) : 0
  if ((second > 0 || left_mute_time === -1)) {
    return {
      left_mute_time,
      second,
    }
  }
  return false
}